import {Component, Inject} from '@angular/core';
import {FormControl} from '@angular/forms';
import {DatePipe} from '@angular/common';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {saveAs} from 'file-saver';
import {take} from 'rxjs/operators';
import {PaymentDTO} from '../../../models/payment/PaymentDTO';
import {SharedService} from '../../../../common/services/shared-service';
import {BankFileService} from '../../../../ent/services/bank-file.service';
import {ReportingService} from '../../../../other/services/reporting.service';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {BankFileEntryDTO} from '../../../../ent/models/BankFileEntryDTO';

enum ReportType {
  BankFile = 'Bank File',
  ProviderRemittanceAdvice = 'Provider Remittance Advice',
  BeneficiaryRemittanceAdvice = 'Beneficiary Remittance Advice',
  PaymentSummary = 'Payment Summary',
}

@Component({
  selector: 'app-payment-report-dialog',
  templateUrl: './payment-report-dialog.component.html',
  styleUrls: [
    './payment-report-dialog.component.scss',
    '../../../../../../styles/global/_dialog-styles.scss'
  ]
})
export class PaymentReportDialogComponent {
  readonly reportType = ReportType;
  heading = 'Payment Report';
  reportTypes: string[] = Object.values(ReportType);
  selectedReportType: string;
  payeeFormControl: FormControl;

  constructor(
    private bankFileService: BankFileService,
    private datePipe: DatePipe,
    private reportingService: ReportingService,
    private sharedService: SharedService,
    @Inject(MAT_DIALOG_DATA) public payment: PaymentDTO
  ) {
    this.selectedReportType = ReportType.BankFile; // default Bank file
    this.payeeFormControl = new FormControl();
  }

  get isNotBankFileOrPaymentSummary(): boolean {
    return this.selectedReportType !== ReportType.BankFile && this.selectedReportType !== ReportType.PaymentSummary
  }

  disableGenerateButton() {
    return this.isNotBankFileOrPaymentSummary && this.payeeFormControl.value == null;
  }

  onReportSelectionChange() {
    this.payeeFormControl.reset();
    this.payeeFormControl.setValue(null);
  }

  getReportTypes(): string[] {
    if (this.payment != null) {
      this.populateReportTypes(this.reportTypes, this.payment.providerPayees, ReportType.ProviderRemittanceAdvice);
      this.populateReportTypes(this.reportTypes, this.payment.beneficiaryPayees, ReportType.BeneficiaryRemittanceAdvice);
    }
    return this.reportTypes;
  }

  onGenerateClick() {
    switch (this.selectedReportType) {
      case ReportType.BankFile: {
        this.generateBankFile();
        break;
      }
      case ReportType.ProviderRemittanceAdvice: {
        this.generateRemittanceAdviceForProvider();
        break;
      }
      case ReportType.BeneficiaryRemittanceAdvice: {
        this.generateRemittanceAdviceForBeneficiary();
        break;
      }
      case ReportType.PaymentSummary: {
        this.generatePaymentSummaryReport();
        break;
      }
    }
  }

  private populateReportTypes(reportTypes: string[], payees: NameValueDTO[], key: string) {
    if (payees == null || payees.length === 0) {
      this.removeItemFromArrayByKey(reportTypes, key);
    }
    else {
      if (this.reportTypes.indexOf(key, 0) === -1) {
        this.reportTypes.push(key);
      }
    }
  }

  private removeItemFromArrayByKey(array: any[], key: any) {
    const index = array.indexOf(key, 0);
    if (index > -1) {
      array.splice(index, 1);
    }
  }

  private generateRemittanceAdviceForProvider() {
    this.reportingService.generateReport('/Reports/Revised_Remittance_Advice', 'pdf', `Payment_Id=${this.payment.paymentId}&Provider_Id=${this.payeeFormControl.value.value}`)
      .pipe(
        take(1),
      )
      .subscribe(
        (response) => {
          saveAs(response, `remittance_advice_provider_${this.payment.paymentId}_${this.datePipe.transform(new Date(), 'yyyyMMdd_HHmm')}.pdf`);
        },
        (_) => {
          this.sharedService.openSnackBar('There was an error generating the report', 'Dismiss');
        }
      );
  }

  private generateRemittanceAdviceForBeneficiary() {
    this.reportingService.generateReport('/Reports/Beneficiary_Remittance_Advice', 'pdf', `Payment_Id=${this.payment.paymentId}&Insured_Person_Id=${this.payeeFormControl.value.value}`)
      .pipe(
        take(1),
      )
      .subscribe(
        (response) => {
          saveAs(response, `remittance_advice_beneficiary_${this.payment.paymentId}_${this.datePipe.transform(new Date(), 'yyyyMMdd_HHmm')}.pdf`);
        },
        (_) => {
          this.sharedService.openSnackBar('There was an error generating the report', 'Dismiss');
        }
      );
  }

  private generatePaymentSummaryReport() {
    this.reportingService.generateReport('/Reports/Payment_Summary', 'pdf', `payment_id=${this.payment.paymentId}`)
      .pipe(
        take(1),
      )
      .subscribe(
        (response) => {
          const fileName = `payment_summary_${this.payment.paymentId}_${this.datePipe.transform(new Date(), 'yyyyMMdd_HHmm')}.pdf`;
          saveAs(response, fileName);
        },
        (_) => {
          this.sharedService.openSnackBar('There was an error generating the report', 'Dismiss');
        }
      );
  }

  private generateBankFile() {
    this.bankFileService
      .getBankFileEntriesByPaymentIdFnb(this.payment.paymentId)
      .pipe(
        take(1),
      )
      .subscribe(
        (response: BankFileEntryDTO[]) => {
          if (response != null && response.length > 0) {
            this.bankFileService.generateBankFile(response, this.payment.bankAccountNumber, this.payment.paymentId);
            this.sharedService.openSnackBar(
              `The banking file has generated successfully`,
              'Dismiss'
            );
          }
          else {
            this.sharedService.openSnackBar(
              `The banking file has failed to generate.`,
              'Dismiss'
            );
          }
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }
}
